import React, { useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import Popover from '@/components/ui/Popover';
import { useMutation } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { GlossaryTermModel, SlideObject } from '@/components/admin/SlideShow/types';
import { IImageModel } from '@/components/admin/SlideShow/stores/useSlideShowStore';
import './VocabTableImage.scss';

const DEFAULT_IMG_SIZE = 70;

interface VocabTableImageProps {
  editModalManager: any;
  glossaryTermModel: GlossaryTermModel;
  hasImage: boolean;
  modalManager: any;
  setGlossaryTermModelId: (num: number) => void;
  setImageModelToEdit: (imageModel: IImageModel) => void;
  slideObject: SlideObject;
}

const VocabTableImage = ({
  editModalManager, glossaryTermModel, hasImage, modalManager, setGlossaryTermModelId, setImageModelToEdit, slideObject,
}: VocabTableImageProps) => {
  const [imgSize, setImgSize] = useState<number | undefined>(
    glossaryTermModel.image_model?.media_meta?.size,
  );

  const updateSlideObject = useSlideShowContext(state => state.updateSlideObject);
  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const locale = useSlideShowContext(state => state.locale);

  const { mutate: deleteGlossaryTermModel } = useMutation({
    mutationFn: () => Axios.delete(Routes.plato_api_image_model_path(glossaryTermModel.image_model?.id)),
    mutationKey: [QueryKeys.GlossaryTerms, QueryKeys.Mutations.Delete, glossaryTermModel.image_model?.id],
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    },
    onSuccess: () => {
      updateSlideObject({ ...slideObject });
    },
  });

  const { mutate: updateImgSize } = useMutation({
    mutationFn: (size: number | undefined) => {
      const mediaMeta = { ...(glossaryTermModel.image_model.media_meta ?? {}) };
      if (size === undefined) {
        delete mediaMeta.size;
      } else {
        mediaMeta.size = size;
      }

      return Axios.put(Routes.plato_api_image_model_path(glossaryTermModel.image_model?.id), {
        image_model: {
          id: glossaryTermModel.image_model?.id,
          media_meta: mediaMeta,
        },
      });
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    },
  });

  const debouncedSizeUpdate = useDebounceCallback(updateImgSize, 300);

  const editImage = () => {
    setImageModelToEdit(glossaryTermModel.image_model);
    setGlossaryTermModelId(glossaryTermModel.id);
    editModalManager.open();
  };

  const { image_en_url: imageEnUrl, image_es_url: imageEsUrl } = glossaryTermModel;
  const src = locale === 'es' && imageEsUrl && !imageEsUrl.includes('missing.png') ? imageEsUrl : imageEnUrl;

  const btnStyles = 'tw-text-3xl tw-border-none tw-bg-inherit tw-cursor-pointer tw-outline-none focus:tw-outline-none';

  return (
    <div
      className="tw-flex tw-flex-col tw-border-solid tw-border-l-[1px] tw-border-r-[1px] tw-border-med-grey
      tw-basis-[24%] tw-justify-center tw-items-center tw-w-40"
    >
      {(hasImage || isPresentMode) ? (
        <Popover>
          <Popover.Trigger
            className="tw-flex tw-items-center tw-justify-center tw-bg-none tw-border-none tw-bg-inherit
                       tw-px-0 tw-py-3 tw-w-[100%] tw-h-[100%]"
            disabled={isPresentMode}
          >
            {hasImage && (
              <img
                alt={`${glossaryTermModel.id}`}
                className="vocab-image tw-max-w-full tw-max-h-full tw-object-cover"
                id={`glossary-term-model-${glossaryTermModel.id}-image`}
                src={src}
                style={imgSize === undefined
                  ? undefined
                  : { '--vocab-image-width': `${imgSize}%` } as React.CSSProperties}
              />
            )}
          </Popover.Trigger>
          <Popover.Content className="tw-flex tw-w-36 tw-h-12 tw-justify-between tw-p-2 tw-gap-1">
            <button
              aria-label="Edit Image"
              className={btnStyles}
              type="button"
              onClick={() => editImage()}
            >
              <i className="fa fa-pencil-square-o tw-text-[#522e91]" />
            </button>
            <button
              aria-label="Delete Image"
              className={btnStyles}
              type="button"
              onClick={() => deleteGlossaryTermModel()}
            >
              <i className="fa fa-trash-o tw-text-[#d2232a]" />
            </button>
            <span className="tw-flex tw-flex-col">
              <label htmlFor="size">Size:</label>
              <div className="tw-flex">
                <input
                  className="tw-rounded tw-border-[1px] tw-border-[#522e91] tw-w-6 tw-h-6"
                  max="100"
                  min="0"
                  name="size"
                  placeholder=" "
                  type="number"
                  value={imgSize ?? DEFAULT_IMG_SIZE}
                  onChange={(e) => {
                    const newValue = Math.max(0, Math.min(100, parseFloat(e.target.value || '0')));
                    const nextSize = newValue === DEFAULT_IMG_SIZE ? undefined : newValue;
                    setImgSize(nextSize);
                    debouncedSizeUpdate(nextSize);
                  }}
                />
                <span className="tw-flex tw-items-center">%</span>
              </div>
            </span>
          </Popover.Content>
        </Popover>
      ) : (
        <button
          className="btn btn--purple btn--sm"
          type="button"
          onClick={() => {
            setGlossaryTermModelId(glossaryTermModel.id);
            modalManager.open();
          }}
        >
          Add Image
        </button>
      )}
    </div>
  );
};

export default VocabTableImage;
